import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { StatusCodeType } from '@fi-shared/types/status-code-type';
import { HttpErrorResponse } from '@angular/common/http';
import { MindMapImportService } from './../mind-map-import/mind-map-import.service';
import { UserService } from '@fi-app/user/user.service';
import { DocumentTemplateService } from '@fi-app/document-template/document-template.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { AppTextService } from '@fi-shared/app-text';
import { AccessRightFiltersComponent, AccessRightFilterSettings } from '@fi-shared/access-right-filters';
import { FileUploadModel } from '@fi-app/utilities/file-upload/file-upload.model';
import { MediaBankFileModel } from '@fi-app/media-bank/media-bank-file.model';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { PublicationSettingModel } from '@fi-shared/models';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { Component, Injector, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { WordImport } from './word-import.model';
import { propComparator } from '@fi-shared/utils/array-sort-comparators';
import { FiDialogsGenericService } from '@fi-shared/fi-dialogs-generic/fi-dialogs-generic.service';
import { IFiDialogProgressResult } from '@fi-shared/fi-dialogs-generic/fi-dialogs-generic';
import { ProgressMessageType } from '@fi-shared/fi-dialogs-generic/progress/progress-message-type';
import { DocumentModel } from '@fi-app/document/document.model';
import { DocumentGeneratePreviewService } from '@fi-app/document/document-generate-preview/document-generate-preview.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { ILightWeightTemplate } from '@fi-app/document-template/document-template';
import { IContentLanguage } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared';
import { IParallelTasksData, parallelTasksQueue } from '@fi-shared/utils/parallel-tasks-queue';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { ObjectTypes } from '@fi-shared/types';

const IMPORT_START_BACKEND_WORK__PROGRESS = 10; // set progress to 10% when start import
const IMPORT_END_BACKEND_WORK__PROGRESS = 50; // set progress to 50% when pages have been created on backend

interface WordImportResult {
    Documents: any[];
    DocumentsCount: number;
    TagsCount: number;
    MediaBankFilesCount: number;
}

@Component({
    selector: 'fi-word-import',
    templateUrl: './word-import.component.html',
    styleUrls: ['./word-import.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-word-import' },
    standalone: false,
})
export class FiWordImportComponent implements OnInit {
    EditTabs: EditTabsModel;
    WordImport: WordImport = new WordImport();
    AccessRightFilterSettings: AccessRightFilterSettings;
    PublicationSetting: PublicationSettingModel = new PublicationSettingModel();
    templatesListData = new DropdownSettingsModelV2<ILightWeightTemplate>({
        filterBy: 'Name',
    });
    usersListData = new DropdownSettingsModelV2<{ Id: number; Name: string }>({
        filterBy: 'Name',
    });
    contentLanguageListData = new DropdownSettingsModelV2<IContentLanguage>({
        filterBy: 'Name',
    });
    File: MediaBankFileModel;
    FileUploadModel: FileUploadModel;

    @ViewChild(AccessRightFiltersComponent, { static: false }) accessRightFilters: AccessRightFiltersComponent;
    private file: File;
    private readonly listDataFilterByPropertyName: string = 'Name';
    private readonly acceptFileType: string = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    constructor(
        private appTextService: AppTextService,
        private templateService: DocumentTemplateService,
        private userService: UserService,
        private mindMapImportService: MindMapImportService,
        private dialogsGenericService: FiDialogsGenericService,
        private contentLanguagesService: ContentLanguagesService,
        private documentGeneratePreviewService: DocumentGeneratePreviewService,
        private injector: Injector,
        private detectDevicesService: DetectDevicesService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit() {
        this.File = new MediaBankFileModel(this.fiFormatDateService);
        this.setPageHeaderData();
        this.setListsDataSettings();
        /*
                const progressData = this.dialogsGenericService.progress(this.appTextService.get('import.importing_pages'));
                progressData.nextMessage({
                    value: 50,
                    messageType: ProgressMessageType.Percentage,
                });
        */
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    save() {
        if (this.file) {
            this.PublicationSetting.ApplyToPublicationItem(this.WordImport);

            const createdByUserObject = this.usersListData.dataSet.find((user: INameIdObj) => {
                return user.Id === this.WordImport.CreatedByUserId;
            });

            this.WordImport.CreatedByUserName = createdByUserObject.Name;

            const progressData = this.dialogsGenericService.progress(this.appTextService.get('import.importing_pages'));
            progressData.nextMessage({
                value: IMPORT_START_BACKEND_WORK__PROGRESS,
                messageType: ProgressMessageType.Percentage,
            });

            progressData.nextMessage({
                value: this.appTextService.get('import.creating_pages'),
                messageType: ProgressMessageType.Text,
            });
            new Promise<WordImportResult>((resolve, reject) => {
                this.mindMapImportService.ImportDocFile(this.WordImport, this.file).then(
                    (result: WordImportResult) => {
                        this.accessRightFilters.OnAccessRightFilterCollectionSaved();

                        progressData.nextMessage({
                            value: IMPORT_END_BACKEND_WORK__PROGRESS,
                            messageType: ProgressMessageType.Percentage,
                        });
                        const documents = result.Documents.map(
                            (doc) => new DocumentModel(doc, undefined, this.injector),
                        );
                        progressData.nextMessage({
                            value: this.appTextService.get('import.generating_previews'),
                            messageType: ProgressMessageType.Text,
                        });
                        this.generateDocumentPreviewImages(
                            documents,
                            this.WordImport.ContentLanguageId,
                            progressData,
                        ).then(() => resolve(result), reject);
                    },
                    (err: HttpErrorResponse) => {
                        const errorText = `${this.appTextService.get('import.failed')}: ${this.getErrorMessage(err)}`;
                        progressData.nextMessage({
                            value: errorText,
                            messageType: ProgressMessageType.Error,
                        });

                        reject();
                    },
                );
            })
                .then((result) => {
                    progressData.nextMessage({
                        value: this.appTextService.get('import.result_success', {
                            '{0}': result.DocumentsCount,
                            '{1}': result.TagsCount,
                            '{2}': result.MediaBankFilesCount,
                        }), // message,
                        messageType: ProgressMessageType.Text,
                    });
                })
                .finally(() => {
                    progressData.complete();
                });
        }
    }

    private getErrorMessage(error: HttpErrorResponse): string {
        if (error.status === StatusCodeType.BadRequest && error.error && typeof error.error.Data === typeof '')
            return error.error.Data;

        return this.appTextService.get('error.unknown');
    }

    private setPageHeaderData() {
        this.EditTabs = new EditTabsModel(
            this.appTextService.get('import.word'),
            undefined,
            this.appTextService.get('common.import'),
        );
        this.EditTabs.Tabs.push(
            new EditTabModel(this.appTextService.get('import.word'), this.appTextService.get('import.word')),
        );
    }

    private setListsDataSettings() {
        this.AccessRightFilterSettings = new AccessRightFilterSettings(
            ModuleType.Document,
            false,
            true,
            ObjectTypes.Document,
        );

        this.contentLanguageListData.setDataSet(this.contentLanguagesService.GetContentLanguages());
        this.WordImport.ContentLanguageId = this.contentLanguagesService.GetDefaultLanguageId();

        this.templateService.GetLightWeightTemplates().then((lightWeightTemplates: ILightWeightTemplate[]) => {
            const dataSet: ILightWeightTemplate[] = lightWeightTemplates
                .sort(propComparator(this.listDataFilterByPropertyName))
                .filter((item) => item.HasViewAccess);
            this.templatesListData.setDataSet(dataSet);
        });

        this.userService.GetLightWeightUsers().then((lightWeightUsers) => {
            let dataSet: { Id: number; Name: string }[] = lightWeightUsers
                .map((user) => ({
                    Id: user.Id,
                    Name: user.FullName,
                }))
                .sort(propComparator(this.listDataFilterByPropertyName));
            this.usersListData.setDataSet(dataSet);
        });

        this.FileUploadModel = new FileUploadModel(($files: FileList) => {
            this.File.PercentageLoaded = 0;

            if (!!$files.length) {
                this.file = $files[0];
                this.File.Name = this.file.name;
                this.File.PercentageLoaded = 100;
            }
        });
        this.FileUploadModel.accept = this.acceptFileType;
    }

    private generateDocumentPreviewImages(
        documents: DocumentModel[],
        languageId: number,
        progressData: IFiDialogProgressResult,
    ) {
        let idx: number = 0;

        return new Promise<void>((resolve, reject) => {
            const requestsQueue: IParallelTasksData = parallelTasksQueue(3);
            documents.forEach((item) => {
                const taskPromiseFn = () => {
                    const taskPromise: Promise<void> = this.documentGeneratePreviewService.generateDocumentPreviewImage(
                        item,
                        languageId,
                    );
                    taskPromise
                        .catch(() => {
                            progressData.nextMessage({
                                value: `Generating preview for document ${item.Id} failed`,
                                messageType: ProgressMessageType.Error,
                            });
                        })
                        .finally(() => {
                            progressData.nextMessage({
                                value:
                                    IMPORT_END_BACKEND_WORK__PROGRESS +
                                    (100 - IMPORT_END_BACKEND_WORK__PROGRESS) * (++idx / documents.length),
                                messageType: ProgressMessageType.Percentage,
                            });

                            if (idx === documents.length) {
                                resolve();
                            }
                        });

                    return taskPromise;
                };

                requestsQueue.add(taskPromiseFn);
            });
        });
    }
}
